// LOT 2 — Queue UI de relecture EC.
//
// Accessible via #admin (hash router simple, pas dans le sidebar public).
// Exigence stricte : aucune action de masse. Un geste = une fiche.
// Tri : urgent (auto_check_passed=false) d'abord, puis P0 avant P1, puis num.
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle, XCircle, LogOut, RefreshCw, ExternalLink, Download } from "lucide-react";
import {
  fetchQueue,
  verifyAccount,
  getAdminToken,
  setAdminToken,
  clearAdminToken,
  downloadExportsZip,
} from "@/lib/adminClient";
import AdminJournal from "@/components/AdminJournal";
import { NATURE_LABELS, SENS_LABELS, formatEUR } from "@/lib/pcgUtils";

const BUCKETS = [
  { key: "urgent", label: "URGENT", subtitle: "audit ∩ auto_check_passed=false" },
  { key: "normal", label: "Normal", subtitle: "P0/P1 ∩ auto_check_passed=true" },
  { key: "metadata", label: "Métadonnée", subtitle: "audit ∩ gravité=none" },
  { key: "all", label: "Tout", subtitle: "union — ta dette totale" },
];

// ── Ligne de la queue à gauche ──────────────────────────────────
const QueueRow = ({ fiche, selected, onClick }) => {
  const isUrgent = fiche.auto_check_passed === false;
  const grav = fiche.audit_gravite;
  const gravBg = grav === "P0" ? "bg-red-100 text-red-900" : grav === "P1" ? "bg-orange-100 text-orange-900" : "bg-slate-100 text-slate-700";
  return (
    <button
      data-testid={`queue-row-${fiche.num}`}
      onClick={onClick}
      className={`w-full text-left px-3 py-2 border-b border-slate-200 transition ${
        selected ? "bg-blue-50" : "bg-white hover:bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {isUrgent && (
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase bg-red-600 text-white px-1.5 py-0.5 rounded" data-testid={`urgent-badge-${fiche.num}`}>
            <AlertTriangle size={10} /> URGENT
          </span>
        )}
        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${gravBg}`}>{grav || "—"}</span>
        <span className="font-mono font-bold text-[#15294F]">{fiche.num}</span>
      </div>
      <div className="text-sm text-slate-800 mt-1 truncate">{fiche.lib}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{fiche.audit_type_erreur || "—"}</div>
    </button>
  );
};

// ── Panneau détail à droite ─────────────────────────────────────
const FicheDetail = ({ fiche, onVerified, onError }) => {
  const [verifying, setVerifying] = useState(false);
  const [msg, setMsg] = useState(null);

  if (!fiche) {
    return (
      <div data-testid="admin-empty-detail" className="p-8 text-slate-500 text-center">
        Sélectionne une fiche dans la liste à gauche pour la relire.
      </div>
    );
  }

  const isUrgent = fiche.auto_check_passed === false;
  const isHighRisk = isUrgent || fiche.audit_gravite === "P0";
  const nature = NATURE_LABELS[fiche.nature] || NATURE_LABELS.mixte;
  const sens = SENS_LABELS[fiche.sens] || SENS_LABELS.M;

  const handleVerify = async () => {
    // Double confirmation renforcée pour P0/URGENT : la fiche est déclarée fausse
    // par l'audit, la contresigner sans correction fige l'erreur.
    const guardMsg = isHighRisk
      ? `⚠ ATTENTION P0/URGENT — la fiche ${fiche.num} est signalée FAUSSE par l'audit.\n\nContresigner telle quelle figera l'erreur dans la base contresignée.\n\nEs-tu vraiment sûr(e) que le contenu actuel est correct malgré le signal d'audit ?`
      : `Contresigner définitivement la fiche ${fiche.num} — ${fiche.lib} ?`;
    if (!window.confirm(guardMsg)) return;
    setVerifying(true);
    setMsg(null);
    try {
      await verifyAccount(fiche.num, "K.T.");
      setMsg({ kind: "ok", text: `✓ ${fiche.num} contresignée.` });
      onVerified(fiche.num);
    } catch (err) {
      setMsg({ kind: "err", text: `Erreur : ${err.message}` });
      if (err.message === "UNAUTHORIZED") onError();
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div data-testid={`admin-detail-${fiche.num}`} className="p-6 space-y-5">
      {/* Header */}
      <div className="pb-4 border-b border-slate-200">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {isUrgent && (
            <span className="inline-flex items-center gap-1 text-xs font-black uppercase bg-red-600 text-white px-2 py-1 rounded">
              <AlertTriangle size={12} /> URGENT — cassé ET faux
            </span>
          )}
          <span className={`text-xs font-black px-2 py-1 rounded ${fiche.audit_gravite === "P0" ? "bg-red-100 text-red-900" : "bg-orange-100 text-orange-900"}`}>
            {fiche.audit_gravite || "META"}
          </span>
          <span className={`text-xs px-2 py-1 rounded border ${nature.color}`}>{nature.label}</span>
          <span className={`text-xs px-2 py-1 rounded border ${sens.color}`}>Sens : {sens.label}</span>
        </div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="font-mono font-black text-4xl md:text-5xl text-[#15294F]">{fiche.num}</span>
          <h2 className="text-xl md:text-2xl font-extrabold text-[#15294F]">{fiche.lib}</h2>
        </div>
      </div>

      {/* Signal audit */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-xs font-bold uppercase tracking-wide text-red-800 mb-2">Signal d&apos;audit</div>
        <div className="text-sm text-red-950 space-y-1">
          <div><span className="font-semibold">Type :</span> <code className="bg-white px-1.5 py-0.5 rounded">{fiche.audit_type_erreur || "—"}</code></div>
          {fiche.review_reason && (
            <div className="text-sm mt-2 italic text-red-900">{fiche.review_reason}</div>
          )}
        </div>
      </div>

      {/* Contenu de la fiche actuelle */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-700 mb-2">Contenu actuel de la fiche</div>
        <p className="text-sm text-slate-800 leading-relaxed mb-3">{fiche.usage}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
            <div className="text-xs font-bold uppercase text-emerald-800 mb-1">Débité quand ?</div>
            <div className="text-emerald-950">{fiche.fonctionnement?.debit || "—"}</div>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded p-3">
            <div className="text-xs font-bold uppercase text-rose-800 mb-1">Crédité quand ?</div>
            <div className="text-rose-950">{fiche.fonctionnement?.credit || "—"}</div>
          </div>
        </div>
        {fiche.exemple && Array.isArray(fiche.exemple.lignes) && (
          <div className="mt-4">
            <div className="text-xs font-bold uppercase text-slate-700 mb-2">Exemple : {fiche.exemple.titre}</div>
            <table className="w-full text-xs border border-slate-200 rounded">
              <thead className="bg-slate-100"><tr><th className="p-2 text-left">Compte</th><th className="p-2 text-left">Libellé</th><th className="p-2 text-right">Débit</th><th className="p-2 text-right">Crédit</th></tr></thead>
              <tbody>
                {fiche.exemple.lignes.map((l, i) => (
                  <tr key={i} className="border-t border-slate-200"><td className="p-2 font-mono">{l.c}</td><td className="p-2">{l.l}</td><td className="p-2 text-right text-emerald-700">{formatEUR(l.d)}</td><td className="p-2 text-right text-rose-700">{formatEUR(l.k)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Actions — UNE FICHE = UN GESTE ─────────────────────────────

          Sur P0/URGENT : le geste par défaut est "Corriger le seed".
          Contresigner sans correction figerait l'erreur.

          Sur P1 / Metadata : la contresignature est en primaire.
      */}
      <div className="border-t border-slate-200 pt-4 space-y-3">
        {isHighRisk && (
          <div
            data-testid={`p0-guard-${fiche.num}`}
            className="bg-red-50 border-2 border-red-400 rounded-lg p-3 text-sm text-red-950"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={18} />
              <div>
                <strong className="font-bold">Fiche P0 — fausse par définition de l&apos;audit.</strong>{" "}
                Le geste par défaut est de <em>corriger le seed puis contresigner la version corrigée</em>.
                Contresigner tel quel figerait l&apos;erreur.
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {/* PRIMARY sur P0/URGENT : Corriger le seed */}
          <a
            data-testid={`fix-seed-btn-${fiche.num}`}
            href={`https://github.com/keyto-cpa/pcg/edit/main/data/seed/pcg/classe-${fiche.classe}.json`}
            target="_blank"
            rel="noreferrer"
            className={
              isHighRisk
                ? "inline-flex items-center gap-2 bg-[#15294F] hover:bg-[#0f1f3d] text-white font-bold px-5 py-2.5 rounded-md text-sm"
                : "inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold px-4 py-2.5 rounded-md text-sm"
            }
          >
            <ExternalLink size={14} /> Corriger le seed (classe-{fiche.classe}.json)
          </a>

          {/* Contresigner — style dépend du risque */}
          <button
            data-testid={`verify-btn-${fiche.num}`}
            onClick={handleVerify}
            disabled={verifying}
            className={
              isHighRisk
                ? "inline-flex items-center gap-2 bg-white border-2 border-emerald-600 hover:bg-emerald-50 disabled:opacity-50 text-emerald-800 font-semibold px-4 py-2.5 rounded-md text-sm"
                : "inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-md text-sm"
            }
          >
            <CheckCircle size={16} />
            {verifying
              ? "Contresignature en cours…"
              : isHighRisk
              ? `Contresigner quand même (K.T.)`
              : `Contresigner ${fiche.num} (K.T.)`}
          </button>

          <a
            data-testid={`open-public-${fiche.num}`}
            href={`#public/${fiche.num}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold px-4 py-2.5 rounded-md text-sm"
          >
            <ExternalLink size={14} /> Ouvrir la fiche publique
          </a>

          <span className="text-xs text-slate-500 self-center ml-auto">
            Aucune action de masse — un geste = une fiche.
          </span>
        </div>
      </div>

      {msg && (
        <div data-testid="verify-message" className={`p-3 rounded text-sm ${msg.kind === "ok" ? "bg-emerald-100 text-emerald-900" : "bg-red-100 text-red-900"}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
};

// ── Login (paste token) ─────────────────────────────────────────
const AdminLogin = ({ onLogin }) => {
  const [t, setT] = useState("");
  return (
    <div data-testid="admin-login" className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="bg-white border border-slate-300 rounded-xl shadow-lg max-w-md w-full p-6">
        <h1 className="text-xl font-extrabold text-[#15294F] mb-1">Queue de relecture EC</h1>
        <p className="text-sm text-slate-600 mb-4">Colle le token admin pour continuer.</p>
        <input
          data-testid="admin-token-input"
          type="password"
          value={t}
          onChange={(e) => setT(e.target.value)}
          placeholder="PCG_ADMIN_TOKEN"
          className="w-full px-3 py-2 border border-slate-300 rounded font-mono text-sm mb-3"
          autoFocus
        />
        <button
          data-testid="admin-login-btn"
          onClick={() => onLogin(t)}
          disabled={!t}
          className="w-full bg-[#15294F] hover:bg-[#0f1f3d] disabled:opacity-50 text-white font-bold py-2.5 rounded"
        >
          Entrer
        </button>
      </div>
    </div>
  );
};

// ── Composant principal ─────────────────────────────────────────
export default function AdminQueue() {
  const [token, setToken] = useState(getAdminToken());
  const [tab, setTab] = useState("queue"); // "queue" | "journal"
  const [bucket, setBucket] = useState("all");
  const [gravite, setGravite] = useState("");
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ urgent: 0, normal: 0, metadata: 0, all: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadExports = async () => {
    setDownloading(true);
    try {
      await downloadExportsZip();
    } catch (e) {
      if (e.message === "UNAUTHORIZED") {
        clearAdminToken();
        setToken("");
        return;
      }
      alert(`Téléchargement échoué : ${e.message}`);
    } finally {
      setDownloading(false);
    }
  };

  const reload = async (nextBucket = bucket, nextGravite = gravite) => {
    setLoading(true);
    setErr(null);
    try {
      // Recharge tous les compteurs + le bucket actif
      const [u, n, m, active] = await Promise.all([
        fetchQueue({ bucket: "urgent", limit: 1 }),
        fetchQueue({ bucket: "normal", limit: 1 }),
        fetchQueue({ bucket: "metadata", limit: 1 }),
        fetchQueue({ bucket: nextBucket, gravite: nextGravite || undefined, limit: 200 }),
      ]);
      setTotals({ urgent: u.total, normal: n.total, metadata: m.total, all: u.total + n.total + m.total });
      setItems(active.items);
      if (active.items.length > 0) {
        // Auto-sélection : garder la même fiche si toujours présente, sinon la 1ère
        const stillThere = selected && active.items.find((f) => f.num === selected.num);
        setSelected(stillThere || active.items[0]);
      } else {
        setSelected(null);
      }
    } catch (e) {
      if (e.message === "UNAUTHORIZED") {
        clearAdminToken();
        setToken("");
        return;
      }
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) reload(bucket, gravite);
  }, [token]);

  const onLogin = (t) => {
    setAdminToken(t);
    setToken(t);
  };

  const onLogout = () => {
    clearAdminToken();
    setToken("");
  };

  const onVerified = (num) => {
    // Retire la fiche localement, recharge compteurs + queue
    setItems((prev) => prev.filter((f) => f.num !== num));
    if (selected?.num === num) setSelected(null);
    reload(bucket, gravite);
  };

  const changeBucket = (b) => {
    setBucket(b);
    setGravite("");
    reload(b, "");
  };

  const total = totals[bucket] ?? totals.all;

  if (!token) return <AdminLogin onLogin={onLogin} />;

  return (
    <div data-testid="admin-queue-root" className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-[#15294F] text-white px-4 md:px-6 py-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide opacity-70">Admin · Relecture EC</div>
          <div className="font-bold text-lg">Queue de relecture — {totals.all} fiches en dette</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-download-exports-btn"
            onClick={handleDownloadExports}
            disabled={downloading}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white px-3 py-1.5 rounded text-sm font-semibold"
            title="Télécharger tous les fichiers d'export GPT (JSON + CSV) dans un zip"
          >
            <Download size={14} /> {downloading ? "Téléchargement…" : "Exports GPT (.zip)"}
          </button>
          {tab === "queue" && (
            <button
              data-testid="admin-refresh-btn"
              onClick={() => reload(bucket, gravite)}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded text-sm"
            >
              <RefreshCw size={14} /> Rafraîchir
            </button>
          )}
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded text-sm"
          >
            <LogOut size={14} /> Fermer session
          </button>
        </div>
      </header>

      {/* Top-level tabs : Queue / Journal */}
      <div className="bg-white border-b border-slate-200 px-4 md:px-6 py-2 flex items-center gap-2">
        <button
          data-testid="admin-tab-queue"
          onClick={() => setTab("queue")}
          className={`px-4 py-1.5 rounded text-sm font-bold transition ${
            tab === "queue" ? "bg-[#15294F] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
          }`}
        >
          Queue de relecture
        </button>
        <button
          data-testid="admin-tab-journal"
          onClick={() => setTab("journal")}
          className={`px-4 py-1.5 rounded text-sm font-bold transition ${
            tab === "journal" ? "bg-[#15294F] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
          }`}
        >
          Journal des contresignatures
        </button>
      </div>

      {tab === "journal" ? (
        <AdminJournal onLogout={onLogout} />
      ) : (
        <>
          {/* Buckets tabs + filter */}
          <div className="bg-white border-b border-slate-200 px-4 md:px-6 py-2 flex items-center gap-2 flex-wrap">
            {BUCKETS.map((b) => (
              <button
                key={b.key}
                data-testid={`bucket-tab-${b.key}`}
                onClick={() => changeBucket(b.key)}
                className={`px-3 py-1.5 rounded text-sm font-semibold transition ${
                  bucket === b.key ? "bg-[#15294F] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {b.label} <span className="opacity-70">({totals[b.key] ?? "—"})</span>
          </button>
        ))}
        {(bucket === "normal" || bucket === "all") && (
          <div className="ml-auto flex items-center gap-2 text-sm">
            <span className="text-slate-500">Filtrer gravité :</span>
            <button
              data-testid="filter-p0"
              onClick={() => { setGravite(gravite === "P0" ? "" : "P0"); reload(bucket, gravite === "P0" ? "" : "P0"); }}
              className={`px-2 py-1 rounded text-xs font-bold ${gravite === "P0" ? "bg-red-600 text-white" : "bg-red-100 text-red-900"}`}
            >P0</button>
            <button
              data-testid="filter-p1"
              onClick={() => { setGravite(gravite === "P1" ? "" : "P1"); reload(bucket, gravite === "P1" ? "" : "P1"); }}
              className={`px-2 py-1 rounded text-xs font-bold ${gravite === "P1" ? "bg-orange-600 text-white" : "bg-orange-100 text-orange-900"}`}
            >P1</button>
          </div>
        )}
      </div>

      {/* 2-cols layout */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[minmax(280px,340px)_1fr] overflow-hidden">
        {/* Left : queue list */}
        <div className="bg-white border-r border-slate-200 overflow-y-auto max-h-[calc(100vh-124px)]">
          {loading && <div className="p-4 text-slate-500 text-sm">Chargement…</div>}
          {err && <div className="p-4 text-red-600 text-sm">{err}</div>}
          {!loading && !err && items.length === 0 && (
            <div data-testid="empty-queue" className="p-6 text-slate-500 text-sm text-center">
              <CheckCircle className="mx-auto mb-2 text-emerald-500" size={32} />
              Queue vide sur ce filtre. Bien joué.
            </div>
          )}
          {items.map((f) => (
            <QueueRow
              key={f.num}
              fiche={f}
              selected={selected?.num === f.num}
              onClick={() => setSelected(f)}
            />
          ))}
          {items.length >= 200 && (
            <div className="p-3 text-xs text-slate-500 border-t border-slate-200">
              200 fiches affichées / {total}. Utilise les filtres pour affiner.
            </div>
          )}
        </div>

        {/* Right : detail */}
        <div className="bg-slate-50 overflow-y-auto max-h-[calc(100vh-124px)]">
          <FicheDetail fiche={selected} onVerified={onVerified} onError={onLogout} />
        </div>
      </div>
        </>
      )}
    </div>
  );
}
